import React from "react";
import type { CSSProperties } from "react";

import Checkbox from "@material-ui/core/Checkbox";
import TableSortLabel from "@material-ui/core/TableSortLabel";

import tableIcons from "../MaterialTableIcons";

import type {
  PaginatedTableQuery,
  TableColumn,
} from "./types";

interface TableHeaderProps<T> {
  columns: TableColumn<T>[];
  query: PaginatedTableQuery;
  onQueryChange: (
    query: PaginatedTableQuery
  ) => void;
  selection: boolean;
  allCurrentPageSelected: boolean;
  someCurrentPageSelected: boolean;
  hasSelectableRows: boolean;
  onToggleSelectAll: () => void;
  headerStyle?: CSSProperties;
}

const SortArrowIcon: React.FC<{
  className: string;
}> = ({ className }) => {
  const SortArrow =
    tableIcons.SortArrow as React.ElementType;

  return (
    <SortArrow
      className={className}
    />
  );
};

const defaultHeaderStyle: CSSProperties = {
  backgroundColor: "#fff",
  color: "#203A75",
  fontWeight: "bold",
  fontSize: "12px",
  borderBottom:"1px solid rgba(224, 224, 224, 1)",
  boxSizing: "border-box",
  verticalAlign: "inherit",
  padding: "6px 24px 6px 16px",
  textAlign: "left",
};

const TableHeader = <T,>({
  columns,
  query,
  onQueryChange,
  selection,
  allCurrentPageSelected,
  someCurrentPageSelected,
  hasSelectableRows,
  onToggleSelectAll,
  headerStyle,
}: TableHeaderProps<T>) => {
  const handleSort = (
    column: TableColumn<T>
  ) => {
    if (column.sortable === false) {
      return;
    }

    const sortField =
      column.sortField ??
      column.field;

    if (!sortField) {
      return;
    }

    const isCurrentField = query.sortField === sortField;

    const nextDirection = isCurrentField && query.sortDirection === "asc" ? "desc" : "asc";

    onQueryChange({
      ...query,
      pageNumber: 1,
      sortField,
      sortDirection:
        nextDirection,
    });
  };

  return (
    <thead>
      <tr>
        {selection && (
          <th
            style={{
              ...defaultHeaderStyle,
              width: 48,
              padding: 0,
              ...headerStyle,
              textAlign: "left",
            }}
          >
            <Checkbox
              size="small"
              color="primary"
              checked={allCurrentPageSelected}
              indeterminate={someCurrentPageSelected &&!allCurrentPageSelected}
              disabled={!hasSelectableRows}
              onChange={onToggleSelectAll}
            />
          </th>
        )}

        {columns.map(
          (
            column,
            index
          ) => {
            const sortField = column.sortField ?? column.field;
            const sortable = column.sortable !== false && Boolean(sortField);
            const isActiveSort = Boolean(sortField) && query.sortField ===sortField;
            const isLastColumn = index === columns.length - 1;

            return (
              <th
                key={`${String(
                  column.field ??
                    "column"
                )}-${index}`}
                style={{
                  ...defaultHeaderStyle,
                  padding: isLastColumn ? "6px 16px" : "6px 24px 6px 16px",
                  whiteSpace: "normal",
                  wordBreak: "normal",
                  ...headerStyle,
                  ...column.headerStyle,
                  textAlign:column.align ?? "left",
                }}
              >
                {sortable ? (
                  <TableSortLabel
                    IconComponent={SortArrowIcon}
                    active={isActiveSort}
                    direction={isActiveSort ? query.sortDirection ?? "asc" : "asc"}
                    onClick={() => handleSort(column)}
                  >
                    {column.title}
                  </TableSortLabel>
                ) : (
                  column.title
                )}
              </th>
            );
          }
        )}
      </tr>
    </thead>
  );
};

export default TableHeader;